import { computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { withDevtools } from '@ngrx-toolkit/core';
import { fromEvent, merge } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import {
  BREAKPOINT_L,
  BREAKPOINT_M,
  BREAKPOINT_S,
  BREAKPOINT_XL,
  COLOR_PICKER_HEIGHT_L,
  COLOR_PICKER_HEIGHT_S,
  COLOR_PICKER_KNOB_SIZE_L,
  COLOR_PICKER_KNOB_SIZE_S,
  COLOR_PICKER_WIDTH_L,
  COLOR_PICKER_WIDTH_S,
} from 'src/app/app.constants';
import { trackEvent } from 'src/app/core/services/google-analytics';
import { GoogleAnalyticsEvent } from 'src/app/shared/enums/google-analytics-event.enum';
import { UiState } from '../models/ui-state.model';
import { RecordingViewMode } from 'src/app/shared/enums/recording-view-mode.enum';
import { FeatureFlag } from 'src/app/shared/enums/feature-flag.enum';
import { FeatureFlagsStore } from './feature-flags.store';
import { VideoStore } from './video.store';
import { PtzStore } from './ptz.store';

const SCREEN_RESIZE_DEBOUNCE = 200;

const RECORDING_VIEW_MODE_KEY = 'recordingViewMode';

/* The simple list is the default; only an explicit choice of the redesigned
 * card switches over, so a cleared or hand-edited storage value falls back to
 * the simple list instead of breaking the list. */
const storedRecordingViewMode = (): RecordingViewMode =>
  localStorage.getItem(RECORDING_VIEW_MODE_KEY) === RecordingViewMode.Extended
    ? RecordingViewMode.Extended
    : RecordingViewMode.Simple;

export const UiStore = signalStore(
  withDevtools('ui'),
  // Factory so the storage/window reads happen at store creation, mirroring
  // the old reducer's initialState.
  withState<UiState>(() => ({
    drawingOverlayVisible: false,
    videoTaggingPanelRolledUp: false,
    screenWidth: window.innerWidth,
    closedIOSPWAFullscreenWarning:
      localStorage.getItem('closedIOSPWAFullscreenWarning') === 'true',
    closedPWAButton: localStorage.getItem('closedPWAButton') === 'true',
    moreSubmenuOpen: false,
    menuCollapsed: sessionStorage.getItem('isMenuCollapsed') === 'true',
    recordingViewModePreference: storedRecordingViewMode(),
  })),
  withProps(() => ({
    _videoStore: inject(VideoStore),
    _ptzStore: inject(PtzStore),
    _featureFlagsStore: inject(FeatureFlagsStore),
  })),
  withComputed(store => ({
    isLargeDevice: computed(() => store.screenWidth() >= BREAKPOINT_M),
    largeDeviceTagging: computed(() => store.screenWidth() >= BREAKPOINT_L),
    sBreakPoint: computed(() => store.screenWidth() < BREAKPOINT_S),
    mBreakPoint: computed(() => store.screenWidth() < BREAKPOINT_M),
    lBreakPoint: computed(() => store.screenWidth() < BREAKPOINT_L),
    xlBreakPoint: computed(() => store.screenWidth() < BREAKPOINT_XL),
    showPWAButton: computed(() => !store.closedPWAButton()),
    /* Switching how the recordings list draws its cards is flag-gated; until
     * the flag is on there is nothing to switch between. */
    recordingViewModeSwitchEnabled: computed(() =>
      store._featureFlagsStore.isEnabled(FeatureFlag.RecordingViewModeSwitch),
    ),
    showIOSPWAWarning: computed(
      () =>
        store._videoStore.isFakeFullscreenActive() &&
        !store.closedIOSPWAFullscreenWarning() &&
        store.closedPWAButton(),
    ),
  })),
  withComputed(store => ({
    isSmallDevice: computed(() => !store.isLargeDevice()),
    /** How the recordings list draws its cards; simple while the flag is off. */
    recordingViewMode: computed(() =>
      store.recordingViewModeSwitchEnabled()
        ? store.recordingViewModePreference()
        : RecordingViewMode.Simple,
    ),
    /* What the recordings toolbar binds to its view switch: the current mode
     * while switching is allowed, null to hide the switch altogether. */
    recordingViewModeSwitch: computed(() =>
      store.recordingViewModeSwitchEnabled()
        ? store.recordingViewModePreference()
        : null,
    ),
    isMenuCollapsed: computed(
      () => store.menuCollapsed() && !store.xlBreakPoint(),
    ),
    videoTaggingEnabled: computed(
      () =>
        store.screenWidth() >= BREAKPOINT_L &&
        !store.drawingOverlayVisible() &&
        !store._ptzStore.overlayVisible(),
    ),
    colorPickerDimensions: computed(() => ({
      height: store.sBreakPoint()
        ? COLOR_PICKER_HEIGHT_S
        : COLOR_PICKER_HEIGHT_L,
      width: store.sBreakPoint() ? COLOR_PICKER_WIDTH_S : COLOR_PICKER_WIDTH_L,
      knowSize: store.sBreakPoint()
        ? COLOR_PICKER_KNOB_SIZE_S
        : COLOR_PICKER_KNOB_SIZE_L,
    })),
  })),
  withMethods(store => ({
    setDrawingOverlayVisibility(visible: boolean) {
      patchState(store, { drawingOverlayVisible: visible });
    },
    toggleVideoTaggingPanel() {
      patchState(store, state => ({
        videoTaggingPanelRolledUp: !state.videoTaggingPanelRolledUp,
      }));
    },
    /* Called when exiting video fullscreen and on navigation. */
    rollDownVideoTaggingPanel() {
      patchState(store, { videoTaggingPanelRolledUp: false });
    },
    closeIOSPWAFullscreenWarning() {
      patchState(store, { closedIOSPWAFullscreenWarning: true });
      localStorage.setItem('closedIOSPWAFullscreenWarning', 'true');
    },
    setClosedPWAButton(value: boolean) {
      patchState(store, { closedPWAButton: value });
      localStorage.setItem('closedPWAButton', value ? 'true' : 'false');
    },
    toggleMoreSubmenu() {
      patchState(store, state => ({ moreSubmenuOpen: !state.moreSubmenuOpen }));
    },
    closeMoreSubmenu() {
      patchState(store, { moreSubmenuOpen: false });
    },
    toggleMenuCollapse() {
      patchState(store, state => ({ menuCollapsed: !state.menuCollapsed }));

      // Persists and tracks the combined value (collapsed && wide screen),
      // matching the old effect that read $isMenuCollapsed after the reducer.
      const collapsed = store.isMenuCollapsed();
      sessionStorage.setItem('isMenuCollapsed', String(collapsed));
      trackEvent(GoogleAnalyticsEvent.TOGGLE_MENU, { collapsed });
    },
    setRecordingViewMode(recordingViewMode: RecordingViewMode) {
      patchState(store, { recordingViewModePreference: recordingViewMode });
      localStorage.setItem(RECORDING_VIEW_MODE_KEY, recordingViewMode);
    },
    setScreenWidth(screenWidth: number) {
      patchState(store, { screenWidth });
    },
  })),
  withHooks({
    onInit(store) {
      const destroyRef = inject(DestroyRef);
      const router = inject(Router);

      merge(fromEvent(window, 'resize'), fromEvent(window, 'orientationchange'))
        .pipe(
          debounceTime(SCREEN_RESIZE_DEBOUNCE),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe(() => store.setScreenWidth(window.innerWidth));

      router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe(() => {
          store.rollDownVideoTaggingPanel();
          if (store.xlBreakPoint()) {
            store.closeMoreSubmenu();
          }
        });
    },
  }),
);
